import React from 'react';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import { DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { antGrey, runwayColors, tagStyle } from '../styles/globalStyles';
import { InlineStepConfigDescription } from '../common/InlineStepConfigDescription';
import { StepGraphStep } from '@/types/workflow';
import { AppNotificationType, AppPlatform, AppStoreType, StepNotificationConfigOverride } from '../../types';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import Popconfirm from 'antd/lib/popconfirm';
import { AppSettingsV2Updatable } from '../../actions/updatable';
import { useIsLoading } from '../../utils/hooks/helpers';
import { useDispatch } from 'react-redux';
import { tryUpdateAppSettingsV2 } from '../../actions/appSettings';

interface NotificationStepConfigOverrideRowProps {
    appId: string;
    orgId: string;
    workflowId: string;
    notificationId: AppNotificationType;
    stepGraphStep: StepGraphStep;
    override: StepNotificationConfigOverride | null;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    onClickSettings: () => void;
}

const NotificationStepConfigOverrideRow: React.FC<NotificationStepConfigOverrideRowProps> = ({
    appId,
    orgId,
    workflowId,
    notificationId,
    stepGraphStep,
    override,
    platform,
    appStoreType,
    onClickSettings,
}) => {
    const dispatch = useDispatch();
    const hasOverride = override != null;
    const deleteKey = `${notificationId}#${stepGraphStep.stepConfigId}`;
    const isDeleting = useIsLoading(AppSettingsV2Updatable.DeleteStepNotificationConfig, deleteKey);

    const handleDelete = () => {
        dispatch(
            tryUpdateAppSettingsV2(
                AppSettingsV2Updatable.DeleteStepNotificationConfig,
                { orgId, appId, workflowId, stepConfigId: stepGraphStep.stepConfigId, notificationId },
                deleteKey,
            ),
        );
    };

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexGrow: 1,
                opacity: hasOverride ? 1 : 0.5,
            }}
        >
            <InlineStepConfigDescription
                stepGraphStep={stepGraphStep}
                platform={platform}
                appStoreType={appStoreType}
                isWorkflowsCIStepEnabled // always true for step config notification overrides
            />
            <Space>
                {hasOverride ?
                    <Tag
                        color={runwayColors.white}
                        style={{
                            ...tagStyle,
                            fontSize: 10,
                            borderColor: override.enabled ? runwayColors.statusGreen : antGrey.grey6,
                            color: override.enabled ? runwayColors.statusGreen : antGrey.grey6,
                        }}
                    >
                        {override.enabled ? 'ENABLED' : 'DISABLED'}
                    </Tag>
                :   <Tag
                        color={runwayColors.white}
                        style={{
                            ...tagStyle,
                            fontSize: 10,
                            borderColor: antGrey.grey6,
                            color: antGrey.grey6,
                        }}
                    >
                        NO OVERRIDE
                    </Tag>
                }
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAUpdateAppSettings}
                    ghost
                    icon={<SettingOutlined style={{ color: antGrey.grey7 }} />}
                    onClick={onClickSettings}
                />
                {hasOverride && (
                    <Popconfirm
                        title={`Remove override for ${stepGraphStep.stepName}?`}
                        description="The notification will use app-level defaults."
                        onConfirm={handleDelete}
                        okText="Remove"
                        okButtonProps={{ danger: true }}
                    >
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                            ghost
                            danger
                            loading={isDeleting}
                            icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                        />
                    </Popconfirm>
                )}
            </Space>
        </div>
    );
};

export default NotificationStepConfigOverrideRow;
